import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  BulkFixRequest, BulkFixPreview, BulkFixBatchDetail,
  BulkFixBatchSummary, StartBulkFixResponse
} from '../interfaces/bulk-fix.interfaces';
import { ScanRunDetailDTO } from '../interfaces/audit.interfaces';

export interface AuditFinding {
  id: number;
  scanRunId: string;
  scanRunAt: string;
  enrollmentType: 'individual' | 'corporate-company' | 'corporate-participant';
  enrollmentId: number;
  corporateEnrollmentId?: number | null;
  participantId?: number | null;
  findingType: string;
  severity: 'high' | 'medium' | 'low';
  title: string;
  detail?: string | null;
  amountDelta?: number | null;
  affectedInstallmentIds?: string | null;
  affectedReceiptIds?: string | null;
  affectedVersionIds?: string | null;
}

export interface AuditTypeBreakdown {
  findingType: string;
  severity: 'high' | 'medium' | 'low';
  count: number;
  affectedEnrollments: number;
  totalAmountDelta: number;
}

export interface AuditSummary {
  lastScanRunId?: string | null;
  lastScanRunAt?: string | null;
  totalFindings: number;
  affectedEnrollments: number;
  highSeverityCount: number;
  mediumSeverityCount: number;
  lowSeverityCount: number;
  byType: AuditTypeBreakdown[];
}

/** Etiquetas humanas para los tipos de hallazgo (espejo del backend). */
export const FindingTypeLabels: Record<string, string> = {
  TOTAL_NO_CUADRA: 'Total no cuadra',
  PAID_AMOUNT_MISMATCH: 'Cuota PAGADA con monto desalineado',
  DUPLICADOS_ACTIVOS: 'Cuotas duplicadas activas',
  CUOTAS_SIN_VERSION: 'Cuotas activas sin versión',
  VERSION_HUERFANA: 'Versión sin cuotas',
  CUOTAS_MULTIVERSION: 'Cuotas activas en varias versiones',
  RECIBO_HUERFANO: 'Recibo huérfano',
  PARTIAL_DEBERIA_SER_PAID: 'Parcial debería ser pagada',
  ARCHIVED_PAID_INSTALLMENT_NOT_VISIBLE: 'Cuota PAGADA archivada (no visible en plan)',
  LINEAGE_DUPLICADO: 'LineageId duplicado entre cuotas activas'
};

@Injectable({ providedIn: 'root' })
export class PaymentPlanAuditService {
  private readonly baseUrl = `${environment.apiUrl}/admin/payment-plan-audit`;

  constructor(private http: HttpClient) {}

  /**
   * 🆕 Encola el scan asíncrono y devuelve `{ scanRunId, status }` inmediatamente.
   * El componente escucha `AuditHubService` para recibir progreso por finding y el
   * evento final de scan terminado.
   */
  runScanAsync(): Observable<{ scanRunId: string; status: string; jobId?: string }> {
    // NOTE: `jobId` queda como opcional solo para retro-compat del componente
    // legacy (será removido cuando Task 17 refactorice el componente).
    return this.http.post<{ scanRunId: string; status: string; jobId?: string }>(
      `${this.baseUrl}/run-async`, {});
  }

  /** 🆕 Cancela un scan en curso. */
  cancelScan(scanRunId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/scans/${scanRunId}/cancel`, {});
  }

  /** 🆕 Devuelve el scan en curso (si lo hay) para reanudar la UI al recargar. */
  getCurrentScan(): Observable<ScanRunDetailDTO | null> {
    return this.http.get<ScanRunDetailDTO | null>(`${this.baseUrl}/scans/current`);
  }

  /** 🆕 Snapshot de progreso de un scan (polling de respaldo si SignalR falla). */
  getScanProgress(scanRunId: string): Observable<ScanRunDetailDTO> {
    return this.http.get<ScanRunDetailDTO>(`${this.baseUrl}/scans/${scanRunId}/progress`);
  }

  // ===== Bulk fix masivo =====

  /** 🆕 Dry-run: cuenta operaciones únicas tras dedup, sin tocar DB. */
  previewBulkFix(req: BulkFixRequest): Observable<BulkFixPreview> {
    return this.http.post<BulkFixPreview>(`${this.baseUrl}/bulk-fix/preview`, req);
  }

  /** 🆕 Encola un bulk fix masivo. Devuelve batchId para suscribir SignalR. */
  startBulkFix(req: BulkFixRequest): Observable<StartBulkFixResponse> {
    return this.http.post<StartBulkFixResponse>(`${this.baseUrl}/bulk-fix`, req);
  }

  /** 🆕 Snapshot del batch (header + items) para polling fallback / page load. */
  getBulkFix(batchId: string): Observable<BulkFixBatchDetail> {
    return this.http.get<BulkFixBatchDetail>(`${this.baseUrl}/bulk-fix/${batchId}`);
  }

  /** 🆕 Cancela un batch en curso. */
  cancelBulkFix(batchId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/bulk-fix/${batchId}/cancel`, {});
  }

  /** 🆕 Histórico de los últimos N batches (header sin items). */
  getBulkFixHistory(take = 50): Observable<BulkFixBatchSummary[]> {
    return this.http.get<BulkFixBatchSummary[]>(`${this.baseUrl}/bulk-fix/history?take=${take}`);
  }

  /** KPIs y breakdown del scan indicado por `scanRunId` (o el más reciente si null). */
  getSummary(scanRunId?: string | null): Observable<{ data: AuditSummary } | AuditSummary> {
    const params: Record<string, string> = {};
    if (scanRunId) params['scanRunId'] = scanRunId;
    return this.http.get<any>(`${this.baseUrl}/summary`, { params });
  }

  /** Listado de hallazgos del scan indicado por `scanRunId` (o el más reciente si null). */
  getFindings(filters: {
    findingType?: string;
    severity?: string;
    enrollmentType?: string;
    enrollmentId?: number;
    take?: number;
    scanRunId?: string | null;
  } = {}): Observable<{ data: AuditFinding[] } | AuditFinding[]> {
    const params: Record<string, string> = {};
    if (filters.findingType) params['findingType'] = filters.findingType;
    if (filters.severity) params['severity'] = filters.severity;
    if (filters.enrollmentType) params['enrollmentType'] = filters.enrollmentType;
    if (filters.enrollmentId != null) params['enrollmentId'] = String(filters.enrollmentId);
    if (filters.take != null) params['take'] = String(filters.take);
    if (filters.scanRunId) params['scanRunId'] = filters.scanRunId;
    return this.http.get<any>(`${this.baseUrl}/findings`, { params });
  }

  /** 🆕 Aplica una corrección a un hallazgo. */
  applyFix(request: ApplyAuditFixRequest): Observable<{ data: ApplyAuditFixResponse } | ApplyAuditFixResponse> {
    return this.http.post<any>(`${this.baseUrl}/apply-fix`, request);
  }

  /** 🆕 Histórico de scans (para selector). */
  getScanRuns(take: number = 50): Observable<{ data: ScanRunMetadata[] } | ScanRunMetadata[]> {
    return this.http.get<any>(`${this.baseUrl}/scan-runs`, { params: { take: String(take) } });
  }

  /**
   * 🆕 Descarga el reporte Excel del scan indicado (o el más reciente si null).
   * Devuelve la respuesta HTTP completa para que el componente lea el filename del header.
   */
  exportExcel(scanRunId?: string | null) {
    const params: Record<string, string> = {};
    if (scanRunId) params['scanRunId'] = scanRunId;
    return this.http.get(`${this.baseUrl}/export`, {
      params,
      responseType: 'blob',
      observe: 'response'
    });
  }
}

/** 🆕 Metadata de un scan histórico. */
export interface ScanRunMetadata {
  scanRunId: string;
  scanRunAt: string;
  totalFindings: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  affectedEnrollments: number;
  isLatest: boolean;
}

// === Acciones de corrección (espejo del backend) ===
export const AuditFixActions = {
  RescueOrphanReceipt: 'RESCUE_ORPHAN_RECEIPT',
  ReactivateArchivedInstallment: 'REACTIVATE_ARCHIVED_INSTALLMENT',
  ConvertPaidToPartial: 'CONVERT_PAID_TO_PARTIAL',
  AdjustPaidAmount: 'ADJUST_PAID_AMOUNT',
  MarkAsPaid: 'MARK_AS_PAID',
  AdjustEnrollmentTotal: 'ADJUST_ENROLLMENT_TOTAL',
  AssignToVersion: 'ASSIGN_TO_VERSION',
  DeleteOrphanVersion: 'DELETE_ORPHAN_VERSION',
  SplitSharedLineage: 'SPLIT_SHARED_LINEAGE'
} as const;

export type AuditFixActionCode = typeof AuditFixActions[keyof typeof AuditFixActions];

export interface ApplyAuditFixRequest {
  findingId?: number | null;
  action: AuditFixActionCode;
  versionReason?: number; // PaymentPlanVersionReason; default Adjustment=5
  comment: string;
  // por acción
  installmentId?: number;
  receiptId?: number;
  newInstallmentAmount?: number;
  newAmountToPay?: number;
  enrollmentId?: number;
  newTotalAmount?: number;
  enrollmentType?: string;
  versionId?: number;
}

export interface ApplyAuditFixResponse {
  success: boolean;
  message: string;
  newVersionId?: number | null;
  newInstallmentId?: number | null;
}

/** Catálogo de acciones disponibles por tipo de hallazgo (para el modal). */
export const FIX_ACTIONS_BY_FINDING_TYPE: Record<string, Array<{ code: AuditFixActionCode; label: string; description: string }>> = {
  // 🆕 La acción PRINCIPAL para cuotas archivadas con recibo: reactivar la cuota
  // existente en lugar de inventar una nueva (evita duplicados).
  ARCHIVED_PAID_INSTALLMENT_NOT_VISIBLE: [
    {
      code: AuditFixActions.ReactivateArchivedInstallment,
      label: 'Reactivar cuota archivada PAGADA',
      description: 'Trae la cuota archivada de vuelta al plan activo en una nueva versión. Si su número ya está ocupado, se renumera automáticamente. NO crea cuotas nuevas — la existente con su recibo se preserva.'
    }
  ],
  RECIBO_HUERFANO: [
    {
      code: AuditFixActions.ReactivateArchivedInstallment,
      label: 'Reactivar la cuota archivada (recomendado)',
      description: 'Si el recibo ya tiene una cuota PAGADA archivada, reactivala en lugar de crear una nueva. Probá esta primero.'
    },
    {
      code: AuditFixActions.RescueOrphanReceipt,
      label: 'Crear cuota PAGADA nueva',
      description: 'Solo si no existe cuota archivada para ese recibo. Crea una nueva cuota PAGADA con el monto del recibo.'
    }
  ],
  PAID_AMOUNT_MISMATCH: [
    {
      code: AuditFixActions.ReactivateArchivedInstallment,
      label: 'Reactivar cuota PAGADA archivada relacionada',
      description: 'Si existe una cuota archivada con recibo asociado, reactivarla resuelve el descuadre sin duplicar.'
    },
    {
      code: AuditFixActions.RescueOrphanReceipt,
      label: 'Crear cuota PAGADA nueva con el recibo huérfano',
      description: 'Útil cuando NO existe cuota archivada disponible y solo hay un recibo sin cuota equivalente.'
    },
    {
      code: AuditFixActions.ConvertPaidToPartial,
      label: 'Convertir a PARCIALMENTE PAGADA',
      description: 'La cuota queda con AmountToPay original; el monto cobrado se refleja como abono parcial. Útil cuando AmountToPay > recibos.'
    },
    {
      code: AuditFixActions.AdjustPaidAmount,
      label: 'Ajustar AmountToPay al monto real cobrado',
      description: 'Mantiene status PAGADA pero cambia AmountToPay para que coincida con la suma de recibos.'
    }
  ],
  PARTIAL_DEBERIA_SER_PAID: [
    {
      code: AuditFixActions.MarkAsPaid,
      label: 'Marcar como PAGADA',
      description: 'La cuota pasa a PAGADA e inmutable. Los recibos ya cubren el monto.'
    }
  ],
  TOTAL_NO_CUADRA: [
    {
      code: AuditFixActions.AdjustEnrollmentTotal,
      label: 'Ajustar TotalAmount de la inscripción',
      description: 'Cambia el valor de matrícula de la inscripción al monto que vos definas (recomendado: suma de cuotas activas).'
    }
  ],
  CUOTAS_SIN_VERSION: [
    {
      code: AuditFixActions.AssignToVersion,
      label: 'Asignar a Snapshot Legacy (v0)',
      description: 'Asocia las cuotas activas sin versión a v0 "Snapshot legacy importado" (o crea v0 si no existe).'
    }
  ],
  VERSION_HUERFANA: [
    {
      code: AuditFixActions.DeleteOrphanVersion,
      label: 'Eliminar versión huérfana',
      description: 'Elimina (soft delete) la versión vacía. No tiene cuotas asociadas, es seguro.'
    }
  ],
  LINEAGE_DUPLICADO: [
    {
      code: AuditFixActions.SplitSharedLineage,
      label: 'Separar LineageId compartido',
      description: 'Deja a una cuota con el lineage y le asigna a las demás un LineageId propio, repuntando sus recibos. In-place, sin nueva versión. Desbloquea refinanciaciones.'
    }
  ]
};
